import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { FormSpy } from 'react-final-form';
import CustomSelect from './CustomSelect';
import { FormGroupContext, FormGroupField } from '../forms/field';

const TaxonomyChildSelectContent = ({
  form,
  values,
  prefix,
  parents,
  parentAttribute,
  childAttribute,
  label,
  labelClass,
  fieldProps
}) => {
  const parentId = prefix
    ? values[prefix]?.[parentAttribute]
    : values[parentAttribute];
  const parent = useMemo(
    () => parents.find(category => category.id === parentId),
    [parents, parentId]
  );
  const children = useMemo(() => (parent ? parent.children : []), [parent]);
  const childIds = useMemo(() => children.map(child => child.id), [children]);
  const childId = prefix
    ? values[prefix]?.[childAttribute]
    : values[childAttribute];
  const fieldName = prefix ? `${prefix}.${childAttribute}` : childAttribute;

  useEffect(() => {
    if (children.length && childId && !childIds.includes(childId)) {
      form.change(fieldName, undefined);
    } else if (children.length === 1) {
      const onlyChildId = children[0].id.toString();

      if (childId !== onlyChildId) {
        form.change(fieldName, onlyChildId);
      }
    }
  }, [form, fieldName, children, childId, childIds]);

  if (children.length <= 1) {
    return null;
  }

  const options = [...children]
    .sort((a, b) => (a.name > b.name ? 1 : -1))
    .map(category => ({ value: category.id, label: category.name }));

  return (
    <>
      <label
        className={labelClass ? labelClass : null}
        htmlFor={childAttribute}
      >
        {label}
      </label>
      <FormGroupField
        {...fieldProps}
        id={childAttribute}
        name={childAttribute}
        component={CustomSelect}
        options={options}
        ariaLabel={label}
        isClearable
      />
    </>
  );
};

TaxonomyChildSelectContent.propTypes = {
  form: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  prefix: PropTypes.string,
  parents: PropTypes.arrayOf(PropTypes.object),
  parentAttribute: PropTypes.string.isRequired,
  childAttribute: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelClass: PropTypes.string,
  fieldProps: PropTypes.object
};

const TaxonomyChildSelect = ({
  parents,
  parentAttribute,
  childAttribute,
  label,
  labelClass,
  ...fieldProps
}) => (
  <FormGroupContext.Consumer>
    {prefix => (
      <FormSpy subscription={{ values: true }}>
        {({ form, values }) => (
          <TaxonomyChildSelectContent
            form={form}
            values={values}
            prefix={prefix}
            parents={parents}
            parentAttribute={parentAttribute}
            childAttribute={childAttribute}
            label={label}
            labelClass={labelClass}
            fieldProps={fieldProps}
          />
        )}
      </FormSpy>
    )}
  </FormGroupContext.Consumer>
);

TaxonomyChildSelect.propTypes = {
  parents: PropTypes.arrayOf(PropTypes.object),
  parentAttribute: PropTypes.string.isRequired,
  childAttribute: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  labelClass: PropTypes.string
};

export default TaxonomyChildSelect;
